import { Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { useVoice, useVoiceCopy } from '@/platform/voice/use-voice';

import { PushIosInstallHint } from './push-ios-hint';
import { PushSettingsGuidePanel } from './push-settings-guide';
import { reportPushActivationError, type PushSettingsGuide } from './push-support';
import {
  declinePushPromptOnDevice,
  dismissPushPromptForSession,
  isPushPromptDeclinedOnDevice,
  isPushPromptLaterDismissed,
} from './push-prompt-preferences';
import { usePushSubscription } from './use-push-subscription';

type PushDevicePromptProps = {
  enabled: boolean;
};

export function PushDevicePrompt({ enabled }: PushDevicePromptProps) {
  const copy = useVoiceCopy();
  const voice = useVoice();
  const {
    supported,
    serverEnabled,
    permission,
    isActiveOnThisDevice,
    currentEndpoint,
    isLoading,
    isEnabling,
    enablePush,
    refreshCurrentEndpoint,
  } = usePushSubscription(enabled);

  const [dismissedLocally, setDismissedLocally] = useState(false);
  const [serviceGuide, setServiceGuide] = useState<PushSettingsGuide | null>(null);

  useEffect(() => {
    if (enabled) {
      void refreshCurrentEndpoint();
    }
  }, [enabled, refreshCurrentEndpoint]);

  const shouldOffer =
    enabled &&
    supported &&
    serverEnabled &&
    permission !== 'denied' &&
    !isActiveOnThisDevice &&
    currentEndpoint == null &&
    !isLoading &&
    !dismissedLocally &&
    !isPushPromptLaterDismissed() &&
    !isPushPromptDeclinedOnDevice();

  const [open, setOpen] = useState(shouldOffer);
  const [offerSynced, setOfferSynced] = useState(shouldOffer);

  if (offerSynced !== shouldOffer) {
    setOfferSynced(shouldOffer);
    setOpen(shouldOffer);
  }

  async function onActivate(permissionPromise: Promise<NotificationPermission>) {
    try {
      await enablePush(permissionPromise);
      setServiceGuide(null);
      setOpen(false);
      toast.success('Push-Benachrichtigungen sind auf diesem Gerät aktiv.');
    } catch (error) {
      if (error instanceof ApiRequestError && error.error.code === 'PUSH_PERMISSION_DENIED') {
        setOpen(false);
        if (Notification.permission === 'denied') {
          toast.error(copy('platform.push.blocked'));
        } else {
          toast.error('Ohne Erlaubnis kann diese App keine Mitteilungen senden.');
        }
        return;
      }
      setServiceGuide(reportPushActivationError(error, voice));
    }
  }

  function onLater() {
    dismissPushPromptForSession();
    setDismissedLocally(true);
    setOpen(false);
  }

  function onDeclineOnDevice() {
    declinePushPromptOnDevice();
    setDismissedLocally(true);
    setOpen(false);
  }

  if (!enabled || !supported) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Push auf diesem Gerät aktivieren?</AlertDialogTitle>
          <AlertDialogDescription>{copy('platform.push.prompt.background')}</AlertDialogDescription>
        </AlertDialogHeader>

        <PushIosInstallHint />
        {serviceGuide ? <PushSettingsGuidePanel guide={serviceGuide} /> : null}

        <AlertDialogFooter className="sm:justify-between">
          <Button type="button" variant="ghost" onClick={onDeclineOnDevice}>
            Nicht auf diesem Gerät
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={onLater}>
              Später
            </Button>
            <Button
              type="button"
              disabled={isEnabling}
              onClick={() => {
                const permissionPromise = Notification.requestPermission();
                void onActivate(permissionPromise);
              }}
            >
              {isEnabling ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
              Aktivieren
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
